import type Phaser from 'phaser';
import type { WeatherState } from './types';
import type { GameContext } from './context';
import { isStoryFlag, STORY_FLAGS, StoryState, type StoryFlagId } from '@/story/flags';
import { resolveEntry } from '@/story/route';
import type { SaveSlot } from '@/save/schema';

export interface PerfSample { fps: number; drawCalls: number; textures: number; listeners: number; timers: number }

export interface DebugApi {
  version: string;
  ready: boolean;
  errors: string[];
  warnings: string[];
  scene(): string | null;
  getState(): unknown;
  jumpTo(flag: string): Promise<boolean>;
  setWeather(state: WeatherState): void;
  clearSaves(): void;
  injectSave(slot: SaveSlot, data: unknown): void;
  skipCinematic(): boolean;
  getPerf(): PerfSample;
  flags: readonly string[];
  routeFor(flag: string): { scene: string; spawn: string } | null;
  isBusy(): boolean;
  /** Resolves when no router transition is in flight (polls). */
  settled(): Promise<void>;
  game: Phaser.Game;
}

declare global {
  interface Window { __GAME_READY__?: boolean; __nantian?: DebugApi }
}

export function installDebugApi(game: Phaser.Game, ctx: GameContext): DebugApi {
  const errors: string[] = [];
  const warnings: string[] = [];
  window.addEventListener('error', (e) => errors.push(String(e.message ?? e)));
  window.addEventListener('unhandledrejection', (e) => errors.push(`unhandledrejection: ${String((e as PromiseRejectionEvent).reason)}`));
  const origWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => { warnings.push(args.map(String).join(' ')); origWarn(...args); };
  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => { errors.push(args.map(String).join(' ')); origError(...args); };

  const api: DebugApi = {
    version: __GAME_VERSION__,
    ready: false,
    errors,
    warnings,
    game,
    scene: () => ctx.router.current()?.scene.key ?? null,
    getState: () => ({
      scene: api.scene(),
      flag: ctx.story.current(),
      next: ctx.story.next(),
      flags: ctx.story.snapshot().flags,
      marks: ctx.story.snapshot().marks,
      seed: ctx.seed,
      weather: ctx.weather,
      settings: ctx.settings,
      position: ctx.position,
    }),
    jumpTo: async (flag: string) => {
      if (!isStoryFlag(flag)) return false;
      await ctx.jumpTo(flag as StoryFlagId);
      return true;
    },
    setWeather: (state) => ctx.setWeather(state),
    clearSaves: () => ctx.saves.clearAll(),
    injectSave: (slot, data) => {
      try { localStorage.setItem(ctx.saves.key(slot), JSON.stringify(data)); } catch { /* ignore */ }
    },
    skipCinematic: () => ctx.skipCinematic(),
    getPerf: () => ({
      fps: Math.round(game.loop.actualFps),
      drawCalls: (game.registry.get('perf.drawCalls') as number | undefined) ?? -1,
      textures: game.textures.list ? Object.keys(game.textures.list).length : -1,
      listeners: ctx.listenerCount(),
      timers: ctx.timerCount(),
    }),
    flags: STORY_FLAGS,
    routeFor: (flag) => {
      if (!isStoryFlag(flag)) return null;
      const s = new StoryState(undefined, { emit: false }); s.jumpTo(flag);
      const e = resolveEntry(s); return { scene: e.scene, spawn: e.spawn };
    },
    isBusy: () => ctx.router.isBusy(),
    settled: () => new Promise<void>((resolve) => { const poll = () => (ctx.router.isBusy() ? setTimeout(poll, 30) : resolve()); poll(); }),
  };
  window.__nantian = api;
  return api;
}

declare const __GAME_VERSION__: string;
